import { IGroupFixture } from "core-integration/src/store/fixtures/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { formatRawAsISO } from "core-integration/src/utils/datetime";
import { formatInTimeZone } from "date-fns-tz";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";
import LiveIndicator from "../img/icons/live-indicator.svg?react";
import Badge from "./Badge";

const StyledFixture = styled.div<IStyledFixtureProps>`
  container-type: inline-size;
  position: relative;
  overflow: auto;
  border-bottom: 1px solid ${({ theme }) => theme.colors.white4};
  padding: 1px 0;
  color: ${({ theme }) => theme.colors.white1};

  ${(props) =>
    props.isOpen &&
    css`
      padding: 0px;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    `}
`;

const Teams = styled.div`
  display: flex;
  margin: 0.7rem 0;
  align-items: center;
`;

const Team = styled.div`
  flex: 1 1 calc(50% - (6.6rem / 2));
  text-align: start;
`;

const TeamH = styled(Team)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: end;
`;

const TeamA = styled(Team)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const TeamName = styled.abbr<IStyledFixtureProps>`
  vertical-align: middle;
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: 18px;
  text-decoration: none;

  @container (min-width: 650px) {
    &:after {
      content: attr(title);
    }
    span {
      display: none;
    }
  }
`;

const FixtureBadge = styled.div`
  display: inline-block;
  margin-inline-end: 0.6rem;
  margin-inline-start: 0.6rem;
  width: 34px;
  height: 34px;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    margin-inline-end: 0.8rem;
    margin-inline-start: 0.8rem;
  }
`;

const FixtureKOTime = styled.time`
  flex: 1 0 6.6rem;
  display: inline-block;
  margin: 0 ${({ theme }) => `${theme.space[2]}`};
  padding: 1rem 1.6rem 0.8rem;
  border: ${({ theme }) => theme.borders[2]};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.1;
  text-align: center;
`;

const FixtureScore = styled.div`
  flex: 1 0 6.6rem;
  display: flex;
  padding: 0.2rem;
  border: ${({ theme }) => theme.borders[2]};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.white4};
`;

const FixtureScoreTeam = styled.span`
  flex: 1;
  padding: ${({ theme }) => `${theme.space[2]} ${theme.space[0]} 0.7rem`};
  border-start-end-radius: ${({ theme }) => theme.radii[0]};
  border-end-end-radius: ${({ theme }) => theme.radii[0]};
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: 1.8rem;
  line-height: 1;
  text-align: center;
  color: ${({ theme }) => theme.colors.white1};
  &:first-child {
    border-inline-end: ${({ theme }) => theme.borders[0]};
  }
`;

const matchTimeIndicatorStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space[1]};
  font-family: ${({ theme }) => theme.fonts.base};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    position: absolute;
    top: 50%;
    inset-inline-start: ${({ theme }) => theme.space[3]};
    transform: translateY(-50%);
    margin-bottom: 0;
  }
`;

const MatchTimeIndicator = styled.span`
  ${matchTimeIndicatorStyles}
`;

const MatchTimeIndicatorMinutes = styled.div`
  ${matchTimeIndicatorStyles}
`;

const LiveContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space[2]};
`;

interface IOwnProps {
  fixture: IGroupFixture;
}

interface IStyledFixtureProps {
  isOpen?: boolean;
}

type Props = IOwnProps & IStyledFixtureProps;

const getMatchStatusElement = (fixture: IGroupFixture) => {
  if (fixture.finished_provisional) {
    return <MatchTimeIndicator>FT</MatchTimeIndicator>;
  }

  if (fixture.started && fixture.team_h_score !== null) {
    return (
      <MatchTimeIndicatorMinutes>
        <LiveContainer>
          <LiveIndicator />
          {fixture.minutes}'
        </LiveContainer>
      </MatchTimeIndicatorMinutes>
    );
  }

  return null;
};

const Fixture = ({ fixture, isOpen }: Props) => {
  const teamsById = useSelector(getTeamsById);
  return (
    <StyledFixture isOpen={isOpen}>
      <Teams>
        <TeamH>
          <TeamName title={teamsById[fixture.team_h].name}>
            <span>{teamsById[fixture.team_h].short_name}</span>
          </TeamName>
          <FixtureBadge>
            <Badge
              team={teamsById[fixture.team_h]}
              isPresentation={true}
              sizes="(min-width: 1024px) 40px, 30px"
            />
          </FixtureBadge>
        </TeamH>
        {fixture.started && fixture.team_h_score !== null ? (
          <FixtureScore>
            <FixtureScoreTeam>{fixture.team_h_score}</FixtureScoreTeam>
            <FixtureScoreTeam>{fixture.team_a_score}</FixtureScoreTeam>
          </FixtureScore>
        ) : fixture.kickoff_time ? (
          <FixtureKOTime dateTime={formatRawAsISO(fixture.kickoff_time)}>
            {formatInTimeZone(
              fixture.kickoffDate,
              Intl.DateTimeFormat().resolvedOptions().timeZone,
              "HH:mm",
            )}
          </FixtureKOTime>
        ) : (
          <FixtureKOTime as="span">-</FixtureKOTime>
        )}
        <TeamA>
          <FixtureBadge>
            <Badge
              team={teamsById[fixture.team_a]}
              isPresentation={true}
              sizes="(min-width: 1024px) 40px, 30px"
            />
          </FixtureBadge>
          <TeamName title={teamsById[fixture.team_a].name}>
            <span>{teamsById[fixture.team_a].short_name}</span>
          </TeamName>
        </TeamA>
      </Teams>
      {getMatchStatusElement(fixture)}
    </StyledFixture>
  );
};

export default Fixture;
